import SecureRoute from '@app/components/SecureRoute'
import ReactiveLayout from '@app/layouts/ReactiveLayout/container/ReactiveLayoutContainer'
import * as React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import ForgotPassword from './Account/ForgotPassword'
import Login from './Account/Login'

// Helper to unwrap React.memo components for react-router Route
const unwrapMemoComponent = (component: any) => {
  if (component && component.$$typeof === Symbol.for('react.memo')) {
    return component.type || component
  }
  return component
}
import GuestEdit from './Administration/Guests/Edit'
import GuestList from './Administration/Guests/List'
import GuestView from './Administration/Guests/View'
import SignTypesList from './Administration/SignTypes/List'
import TeamsList from './Administration/Teams/List'
import TeamView from './Administration/Teams/View'
import { UserAdd, UserEdit } from './Administration/Users/Edit'
import UsersList from './Administration/Users/List'
import UserView from './Administration/Users/View'
import TutorialsList from './Tutorials/List'
import LeadsList from './Brokerage/Leads/List'
import ListingEdit from './Brokerage/Listings/Edit'
import ListingsList from './Brokerage/Listings/List'
import ListingView from './Brokerage/Listings/View'
import PipelineEdit from './Brokerage/Pipeline/Edit'
import PipelineList from './Brokerage/Pipeline/List'
import PipelineView from './Brokerage/Pipeline/View'
import SuccessStoriesList from './Properties/SuccessStories/List'
import SuccessStoriesEdit from './Properties/SuccessStories/Edit'
import AssignmentsCalendar from './Calendars/Assignments'
import BrokeragePipelinesCalendar from './Calendars/BrokeragePipelines'
import DueDiligenceCalendar from './Calendars/DueDiligence'
import ListingsCalendar from './Calendars/Listings'
import ConnectionEdit from './Connections/Edit'
import ConnectionsList from './Connections/List'
import ConnectionView from './Connections/View'
import CityEdit from './Development/Cities/Edit'
import Cities from './Development/Cities/List'
import CityView from './Development/Cities/View'
import DevelopmentClientEdit from './Development/Clients/Edit'
import ClientList from './Development/Clients/List'
import DevelopmentSiteEdit from './Development/Clients/Sites/Edit'
import SiteList from './Development/Clients/Sites/List'
import {
  DevelopmentSiteRedirect,
  DevelopmentSiteView,
} from './Development/Clients/Sites/View'
import DevelopmentClientDetail from './Development/Clients/View'
import EarnestMoneyList from './Development/EarnestMoney/List'
import DevelopmentProjects from './Development/Projects/List'
import FeedItemSearch from './FeedItem/Search'
import FeedItemDetail from './FeedItem/View'
import FormsList from './Forms/List'
import Home from './Home'
import LandingPage from './LandingPage'
import MaintenancePage from './Maintenance/MaintenancePage'
import MapViewPage from './Map'
import NotificationsList from './Notifications/List'
import AssignmentEdit from './Personal/Assignments/Edit'
import Assignments from './Personal/Assignments/List'
import AssignmentDetail from './Personal/Assignments/View'
import GoalList from './Personal/Goals/List'
import MessageList from './Personal/Messages/List'
import TodoList from './Personal/Todos/List'
import PropertyEdit from './Properties/Properties/Edit'
import PropertiesList from './Properties/Properties/List'
import PropertyView from './Properties/Properties/View'
import EarnestMoneyReport from './Reports/EarnestMoney/List'
import ProjectedEarnings from './Reports/ProjectedEarnings/List'
import ProjectedProfit from './Reports/ProjectedProfit/List'
import SignsList from './Reports/Signs/List'
import SoldPropertiesList from './Reports/SoldProperties/List'
import SearchPage from './Search'
import ChatPage from './Chat'

const DefaultRoute = () => (
  <ReactiveLayout>
    <Switch>
      <SecureRoute
        exact
        path='/'
        component={LandingPage}
        brokerage
        development
        guest
      />
      <SecureRoute exact path='/home' component={Home} brokerage development />
      <SecureRoute
        exact
        path='/connection/add'
        component={ConnectionEdit}
        brokerage
        development
      />
      <SecureRoute
        exact
        path='/connection/:id/edit'
        component={ConnectionEdit}
        brokerage
        development
      />
      <SecureRoute
        path='/connection/:id'
        component={ConnectionView}
        brokerage
        development
      />
      <SecureRoute
        exact
        path='/connections'
        component={ConnectionsList}
        brokerage
        development
      />
      <SecureRoute
        exact
        path='/assignment/add'
        component={AssignmentEdit}
        brokerage
        development
        guest
      />
      <SecureRoute
        exact
        path='/assignment/:id/edit'
        component={AssignmentEdit}
        brokerage
        development
      />
      <SecureRoute
        path='/assignment/:id'
        component={AssignmentDetail}
        brokerage
        development
        guest
      />
      <SecureRoute
        path='/map'
        component={MapViewPage}
        brokerage
        development
        guest
      />
      <SecureRoute
        path='/assignments'
        component={Assignments}
        brokerage
        development
        guest
      />
      <SecureRoute path='/goals' component={GoalList} brokerage development />
      <SecureRoute
        path='/messages'
        component={MessageList}
        brokerage
        development
      />
      <SecureRoute path='/todos' component={TodoList} brokerage development />
      <SecureRoute
        exact
        path='/properties/property/add'
        component={PropertyEdit}
        brokerage
      />
      <SecureRoute
        exact
        path='/properties/properties'
        component={PropertiesList}
        brokerage
      />
      <SecureRoute
        exact
        path='/properties/property/:id/edit'
        component={PropertyEdit}
        brokerage
      />
      <SecureRoute
        path='/properties/property/:id'
        component={PropertyView}
        brokerage
      />
      <Route
        exact
        path='/property/:id'
        render={(props) => (
          <Redirect to={`/properties/property/${props.match.params.id}`} />
        )}
      />
      <Route
        exact
        path='/properties/property//:id'
        render={(props) => (
          <Redirect to={`/properties/property/${props.match.params.id}`} />
        )}
      />
      <SecureRoute
        path='/development/city/:citySlug/edit'
        component={CityEdit}
        development
      />
      <SecureRoute
        path='/development/city/add'
        component={CityEdit}
        development
      />
      <SecureRoute
        path='/development/city/:citySlug'
        component={CityView}
        development
      />
      <SecureRoute path='/development/cities' component={Cities} development />
      <SecureRoute
        path='/development/client/:clientSlug/edit'
        component={DevelopmentClientEdit}
        development
        guest
      />
      <SecureRoute
        path='/development/client/add'
        component={DevelopmentClientEdit}
        development
        guest
      />
      <SecureRoute
        path='/development/client/:clientSlug/site/:siteSlug/edit'
        component={DevelopmentSiteEdit}
        development
        guest
      />
      <SecureRoute
        path='/development/client/:clientSlug/site/:siteSlug'
        component={DevelopmentSiteView}
        development
        guest
      />
      <SecureRoute
        path='/development/client/:clientSlug/sites/add'
        component={DevelopmentSiteEdit}
        development
        guest
      />
      <SecureRoute
        path='/development/clients/:slug/sites'
        component={SiteList}
        development
        guest
      />
      <SecureRoute
        path='/development/client/:clientSlug'
        component={DevelopmentClientDetail}
        development
        guest
      />
      <SecureRoute
        path='/development/clients'
        component={ClientList}
        development
        guest
      />
      <SecureRoute
        path='/development/myProjects'
        component={DevelopmentProjects}
        development
        guest
      />
      <SecureRoute
        path='/development/earnestMoney'
        component={EarnestMoneyList}
        development
      />
      <SecureRoute
        path='/development/sites/:id'
        component={DevelopmentSiteRedirect}
        development
        guest
      />
      <SecureRoute
        path='/admin/reports/earnestMoney'
        component={EarnestMoneyReport}
      />
      <SecureRoute
        path='/admin/reports/potentialProfit'
        component={ProjectedProfit}
      />
      <SecureRoute
        path='/admin/reports/earnings'
        component={ProjectedEarnings}
      />
      <SecureRoute
        path='/admin/reports/signs'
        component={SignsList}
        brokerage
      />
      <SecureRoute
        path='/admin/reports/soldProperties'
        component={SoldPropertiesList}
        brokerage
      />
      <SecureRoute path='/admin/teams' component={TeamsList} />
      <SecureRoute path='/admin/team/:id' component={TeamView} />
      <SecureRoute path='/admin/users' component={UsersList} />
      <SecureRoute exact path='/admin/user/add' component={UserAdd} />
      <SecureRoute exact path='/admin/user/:id/edit' component={UserEdit} />
      <SecureRoute path='/admin/user/:id' component={UserView} />
      <SecureRoute path='/admin/guests' component={GuestList} />
      <SecureRoute path='/admin/guest/:id/edit' component={GuestEdit} />
      <SecureRoute path='/admin/guest/:id' component={GuestView} />
      <SecureRoute path='/admin/manageSignTypes' component={SignTypesList} />
      <SecureRoute
        path='/brokerage/leads'
        component={LeadsList}
        brokerage
      />
      <SecureRoute
        path='/brokerage/listings'
        component={ListingsList}
        brokerage
      />
      <SecureRoute
        exact
        path='/brokerage/listing/new'
        component={ListingEdit}
        brokerage
      />
      <SecureRoute
        exact
        path='/brokerage/listing/:id/edit'
        component={ListingEdit}
        brokerage
      />
      <SecureRoute
        path='/brokerage/listing/:id'
        component={ListingView}
        brokerage
      />
      <SecureRoute
        exact
        path='/brokerage/pipeline'
        component={PipelineList}
        brokerage
      />
      <SecureRoute
        exact
        path='/brokerage/pipeline/new'
        component={PipelineEdit}
        brokerage
      />
      <SecureRoute
        exact
        path='/brokerage/pipeline/:id/edit'
        component={PipelineEdit}
        brokerage
      />
      <SecureRoute
        path='/brokerage/pipeline/:id'
        component={PipelineView}
        brokerage
      />
      <SecureRoute
        exact
        path='/properties/successStories'
        component={SuccessStoriesList}
        guest
        brokerage
        development
      />
      <SecureRoute
        exact
        path='/properties/successStory/add'
        component={SuccessStoriesEdit}
        guest
        brokerage
        development
      />
      <SecureRoute
        path='/properties/successStory/:id'
        component={SuccessStoriesEdit}
        brokerage
      />
      <SecureRoute
        path='/properties/successStory/:id/edit'
        component={SuccessStoriesEdit}
        brokerage
      />
      <SecureRoute
        path='/calendars/assignments'
        component={AssignmentsCalendar}
        brokerage
        development
        guest
      />
      <SecureRoute
        path='/calendars/brokeragePipelines'
        component={BrokeragePipelinesCalendar}
        brokerage
      />
      <SecureRoute
        path='/calendars/dueDiligence'
        component={DueDiligenceCalendar}
        development
        guest
      />
      <SecureRoute
        path='/calendars/listings'
        component={ListingsCalendar}
        brokerage
      />
      <SecureRoute
        exact
        path='/feed/search'
        component={FeedItemSearch}
        brokerage
        development
      />
      <SecureRoute
        path='/feed/:id'
        component={FeedItemDetail}
        brokerage
        development
      />
      <SecureRoute path='/forms' component={FormsList} brokerage development />
      <SecureRoute
        path='/notifications'
        component={NotificationsList}
        brokerage
        development
        guest
      />
      <SecureRoute
        path='/search'
        component={SearchPage}
        brokerage
        development
        guest
      />
      <SecureRoute path='/tutorials' component={TutorialsList} brokerage development />
      <SecureRoute path='/chat' component={ChatPage} brokerage development />
      <SecureRoute component={MaintenancePage} brokerage development guest />
    </Switch>
  </ReactiveLayout>
)

export const routes = (
  <Switch>
    <Route path='/login' component={unwrapMemoComponent(Login)} />
    <Route path='/forgotPassword' component={unwrapMemoComponent(ForgotPassword)} />
    <Route component={DefaultRoute} />
  </Switch>
)
